import { interpretYesavageLabel } from "./scales-data";

// ============ AURORA CARE - CLINICAL RECOMMENDATIONS ENGINE ============
// Based on international geriatric guidelines:
// - DASH Diet, Mediterranean Diet, ESPEN Guidelines
// - WHO Physical Activity Guidelines for Older Adults
// - CDC STEADI Fall Prevention
// - APTA Geriatric Physical Therapy Guidelines
// - AOTA Occupational Therapy Guidelines

// ============ GERIATRIC SYNDROMES DETECTION ============
export const GERIATRIC_SYNDROMES = {
  fragilidad: {
    id: "fragilidad",
    name: "Fragilidad",
    description: "Síndrome caracterizado por pérdida de reserva fisiológica",
    triggers: [
      { scale: "frail", condition: (score) => score >= 3 },
      { scale: "cfs", condition: (score) => score >= 5 },
      { scale: "barthel", condition: (score) => score < 60 }
    ]
  },
  riesgo_caidas: {
    id: "riesgo_caidas",
    name: "Riesgo de Caídas",
    description: "Alto riesgo de caídas con potencial fractura",
    triggers: [
      { scale: "morse", condition: (score) => score >= 45 },
      { scale: "tinetti", condition: (score) => score < 19 },
      { scale: "tug", condition: (score) => Number(score) >= 20 }
    ]
  },
  deterioro_cognitivo: {
    id: "deterioro_cognitivo",
    name: "Deterioro Cognitivo",
    description: "Déficit cognitivo que afecta funcionalidad",
    triggers: [
      { scale: "mmse", condition: (score) => score < 24 },
      { scale: "pfeiffer", condition: (score) => score >= 3 }
    ]
  },
  delirium_cam: {
    id: "delirium_cam",
    name: "Sospecha de delirium (CAM)",
    description: "Criterios CAM cumplidos; valorar causa orgánica, medicamentos y entorno",
    triggers: [
      {
        scale: "cam",
        condition: (score) => score === true || score === 1 || score === "1",
      },
    ],
  },
  malnutricion: {
    id: "malnutricion",
    name: "Malnutrición / Riesgo Nutricional",
    description: "Estado nutricional comprometido",
    triggers: [
      { scale: "mna_sf", condition: (score) => score < 12 }
    ]
  },
  depresion: {
    id: "depresion",
    name: "Depresión Geriátrica",
    description: "Síntomas depresivos significativos",
    triggers: [
      { scale: "yesavage", condition: (score) => score >= 5 }
    ]
  },
  ansiedad: {
    id: "ansiedad",
    name: "Ansiedad clínicamente significativa",
    description: "Síntomas ansiosos que pueden requerir evaluación o seguimiento",
    triggers: [{ scale: "gad7", condition: (score) => Number(score) >= 10 }]
  },
  dependencia_funcional: {
    id: "dependencia_funcional",
    name: "Dependencia Funcional",
    description: "Limitación en actividades de la vida diaria",
    triggers: [
      { scale: "barthel", condition: (score) => score < 60 },
      { scale: "katz", condition: (score) => score < 4 },
      { scale: "lawton", condition: (score) => score < 4 }
    ]
  },
  riesgo_upp: {
    id: "riesgo_upp",
    name: "Riesgo de Úlceras por Presión",
    description: "Alto riesgo de desarrollar lesiones cutáneas",
    triggers: [
      { scale: "norton", condition: (score) => score < 14 }
    ]
  },
  aislamiento_social: {
    id: "aislamiento_social",
    name: "Riesgo Social",
    description: "Situación sociofamiliar de riesgo",
    triggers: [
      { scale: "gijon", condition: (score) => score >= 10 }
    ]
  },
  polifarmacia_simple: {
    id: "polifarmacia_simple",
    name: "Polifarmacia (≥5 medicamentos)",
    description:
      "Recomendaciones: revisar lista completa y vigente; valorar interacciones y duplicidad terapéutica; considerar deprescripción guiada según criterio clínico (p. ej. STOPP/START u otras guías); reforzar educación y adherencia si hay dificultad declarada.",
    triggers: [{ scale: "polifarmacia_simple", condition: (score) => Number(score) >= 5 }],
  },
};

// Function to detect syndromes based on scale scores
export const detectGeriatricSyndromes = (scaleScores) => {
  const detectedSyndromes = [];
  
  Object.values(GERIATRIC_SYNDROMES).forEach(syndrome => {
    const isTriggered = syndrome.triggers.some(trigger => {
      const score = scaleScores[trigger.scale];
      return score !== undefined && score !== null && trigger.condition(score);
    });
    
    if (isTriggered) {
      detectedSyndromes.push({
        ...syndrome,
        triggeredBy: syndrome.triggers
          .filter(t => {
            const score = scaleScores[t.scale];
            return score !== undefined && score !== null && t.condition(score);
          })
          .map(t => t.scale)
      });
    }
  });
  
  return detectedSyndromes;
};

// ============ NUTRITION RECOMMENDATIONS (ESPEN, DASH, Mediterranean) ============
export const NUTRITION_LIBRARY = {
  general_geriatrico: {
    id: "general_geriatrico",
    name: "Recomendaciones Generales para Adulto Mayor",
    source: "ESPEN Guidelines on Nutrition in Dementia 2015, ESPEN Guideline on Clinical Nutrition and Hydration in Geriatrics 2019",
    recommendations: [
      "Consumo proteico: 1.0-1.2 g/kg/día (aumentar a 1.2-1.5 g/kg si fragilidad o enfermedad aguda)",
      "Distribución de proteínas: 25-30g por comida principal",
      "Hidratación: 30 ml/kg/día (mínimo 1.5-2 litros)",
      "Suplementación Vitamina D: 800-1000 UI/día",
      "Comidas frecuentes: 5-6 comidas pequeñas al día",
      "Texturas adaptadas según capacidad masticatoria",
      "Ambiente agradable durante las comidas"
    ]
  },
  malnutricion: {
    id: "malnutricion",
    name: "Plan para Malnutrición",
    source: "ESPEN Guidelines 2019",
    triggers: ["mna_sf < 8", "IMC < 22"],
    recommendations: [
      "URGENTE: Suplemento nutricional oral (SNO) 400-600 kcal/día adicionales",
      "Aumentar proteínas a 1.5 g/kg/día",
      "Enriquecer comidas con aceite de oliva, leche en polvo, huevo",
      "Considerar SNO hiperproteico entre comidas",
      "Evaluar causa de malnutrición (depresión, disfagia, dentadura)",
      "Control de peso semanal",
      "Derivar a nutricionista para plan individualizado"
    ]
  },
  riesgo_nutricional: {
    id: "riesgo_nutricional",
    name: "Plan para Riesgo Nutricional",
    source: "ESPEN Guidelines 2019",
    triggers: ["mna_sf 8-11"],
    recommendations: [
      "Suplemento nutricional oral (SNO) 200-400 kcal/día",
      "Asegurar proteínas en cada comida (huevo, carnes magras, legumbres)",
      "Enriquecer caldos y purés con proteínas",
      "Snacks proteicos: yogur, frutos secos, queso",
      "Control de peso quincenal",
      "Evaluar factores de riesgo modificables"
    ]
  },
  hipertension: {
    id: "hipertension",
    name: "Dieta DASH para Hipertensión",
    source: "DASH Diet - NIH Guidelines",
    triggers: ["HTA", "PA > 140/90"],
    recommendations: [
      "Reducir sodio a < 2300 mg/día (ideal < 1500 mg)",
      "Aumentar potasio: plátano, naranja, espinaca, tomate",
      "5+ porciones de frutas y verduras al día",
      "Lácteos bajos en grasa",
      "Granos integrales",
      "Limitar carnes rojas y procesadas",
      "Evitar alimentos ultraprocesados",
      "Cocinar sin sal, usar especias y hierbas"
    ]
  },
  diabetes: {
    id: "diabetes",
    name: "Plan Nutricional para Diabetes",
    source: "ADA Standards of Care 2024",
    triggers: ["DM2", "Glicemia > 126"],
    recommendations: [
      "Carbohidratos complejos de bajo índice glucémico",
      "Evitar azúcares simples y bebidas azucaradas",
      "Fibra: 25-30 g/día (avena, legumbres, verduras)",
      "Horarios regulares de comidas",
      "Control de porciones de carbohidratos",
      "Frutas enteras, no en jugo",
      "Grasas saludables: aceite de oliva, aguacate, nueces"
    ]
  },
  insuficiencia_renal: {
    id: "insuficiencia_renal",
    name: "Dieta para Enfermedad Renal Crónica",
    source: "KDOQI Guidelines",
    triggers: ["ERC", "TFG < 60"],
    recommendations: [
      "Proteínas: 0.6-0.8 g/kg/día (ajustar según TFG)",
      "Control de potasio según niveles séricos",
      "Limitar fósforo: evitar lácteos y refrescos cola",
      "Restricción de sodio según retención",
      "Líquidos según diuresis",
      "Suplementación según deficiencias",
      "Coordinación con nefrología"
    ]
  },
  mediterranea: {
    id: "mediterranea",
    name: "Dieta Mediterránea",
    source: "Mediterranean Diet - PREDIMED Study",
    recommendations: [
      "Aceite de oliva virgen extra como grasa principal",
      "Abundantes frutas, verduras y legumbres",
      "Pescado 2-3 veces por semana",
      "Frutos secos diarios (30g)",
      "Carnes rojas: máximo 1-2 veces/semana",
      "Vino tinto moderado (opcional, 1 copa con comida)",
      "Cereales integrales",
      "Hierbas aromáticas en lugar de sal"
    ]
  }
};

// ============ FALL PREVENTION RECOMMENDATIONS (CDC STEADI, WHO) ============
export const FALL_PREVENTION_LIBRARY = {
  bajo_riesgo: {
    id: "bajo_riesgo",
    name: "Prevención Básica de Caídas",
    source: "CDC STEADI, WHO Guidelines",
    triggers: ["morse < 25", "tinetti >= 24"],
    recommendations: [
      "Ejercicio regular: caminata 30 min/día",
      "Revisión anual de visión y audición",
      "Revisión de medicamentos (sedantes, hipotensores)",
      "Calzado cerrado antideslizante",
      "Iluminación adecuada en casa",
      "Evitar alfombras sueltas"
    ]
  },
  riesgo_moderado: {
    id: "riesgo_moderado",
    name: "Intervención para Riesgo Moderado de Caídas",
    source: "CDC STEADI, APTA Guidelines",
    triggers: ["morse 25-44", "tinetti 19-23"],
    recommendations: [
      "Programa de ejercicios de equilibrio supervisado",
      "Tai Chi o yoga adaptado (2-3 veces/semana)",
      "Fortalecimiento de miembros inferiores",
      "Evaluación de visión y corrección",
      "Revisión de medicamentos con potencial de caídas",
      "Valoración de hipotensión ortostática",
      "Barras de apoyo en baño",
      "Evitar levantarse bruscamente"
    ]
  },
  alto_riesgo: {
    id: "alto_riesgo",
    name: "Intervención Intensiva Alto Riesgo de Caídas",
    source: "CDC STEADI, WHO Fall Prevention Guidelines",
    triggers: ["morse >= 45", "tinetti < 19"],
    recommendations: [
      "URGENTE: Derivar a fisioterapia para programa especializado",
      "Programa OTAGO o similar de ejercicios domiciliarios",
      "Uso de andador o bastón según evaluación",
      "Protector de cadera si muy alto riesgo de fractura",
      "Revisión URGENTE de medicamentos",
      "Modificaciones del hogar:",
      "  - Barras en baño y pasillos",
      "  - Eliminar obstáculos y cables",
      "  - Luz nocturna en dormitorio y baño",
      "  - Antideslizante en bañera",
      "  - Cama a altura adecuada",
      "Calzado terapéutico",
      "Acompañamiento en deambulación",
      "Considerar cama baja o colchón en suelo si muy alto riesgo"
    ]
  }
};

// ============ PHYSICAL THERAPY RECOMMENDATIONS (WHO, APTA) ============
export const EXERCISE_LIBRARY = {
  general: {
    id: "general",
    name: "Actividad Física para Adulto Mayor",
    source: "WHO Physical Activity Guidelines for Older Adults 2020",
    recommendations: [
      "150-300 minutos/semana de actividad aeróbica moderada",
      "O 75-150 minutos/semana de actividad vigorosa",
      "Fortalecimiento muscular 2+ días/semana",
      "Ejercicios de equilibrio 3+ días/semana",
      "Limitar tiempo sedentario",
      "Alguna actividad es mejor que ninguna",
      "Empezar gradualmente, aumentar progresivamente"
    ]
  },
  fragilidad: {
    id: "fragilidad",
    name: "Programa de Ejercicio para Fragilidad",
    source: "APTA Geriatric Guidelines, VIVIFRAIL",
    triggers: ["frail >= 3", "cfs >= 5"],
    recommendations: [
      "Programa multicomponente supervisado",
      "Ejercicios de resistencia progresiva:",
      "  - Sentadillas con apoyo",
      "  - Levantarse de silla sin manos",
      "  - Flexiones de pared",
      "Ejercicios de equilibrio:",
      "  - Posición tándem",
      "  - Apoyo unipodal",
      "  - Caminar en línea",
      "Ejercicios de flexibilidad",
      "Caminata progresiva (iniciar 5-10 min)",
      "Frecuencia: 3-5 veces/semana",
      "Duración: 30-45 minutos por sesión"
    ]
  },
  dependencia_moderada: {
    id: "dependencia_moderada",
    name: "Ejercicios para Dependencia Funcional",
    source: "APTA Guidelines",
    triggers: ["barthel 40-60"],
    recommendations: [
      "Ejercicios en sedestación:",
      "  - Marcha en sitio sentado",
      "  - Elevación de piernas",
      "  - Círculos de tobillo",
      "Transferencias supervisadas",
      "Bipedestación con apoyo",
      "Ejercicios de miembros superiores",
      "Actividades de la vida diaria como ejercicio",
      "Fisioterapia domiciliaria recomendada"
    ]
  },
  post_caida: {
    id: "post_caida",
    name: "Rehabilitación Post-Caída",
    source: "APTA Guidelines",
    recommendations: [
      "Evaluación completa de la caída",
      "Programa gradual de recuperación de confianza",
      "Ejercicios de equilibrio progresivos",
      "Práctica de levantarse del suelo",
      "Estrategias de protección en caídas",
      "Considerar dispositivo de alerta médica"
    ]
  }
};

// ============ COGNITIVE STIMULATION (AOTA) ============
export const COGNITIVE_LIBRARY = {
  prevencion: {
    id: "prevencion",
    name: "Estimulación Cognitiva Preventiva",
    source: "AOTA Guidelines",
    triggers: ["mmse >= 24"],
    recommendations: [
      "Lectura diaria (30 min)",
      "Crucigramas, sudoku, juegos de palabras",
      "Aprender algo nuevo (idioma, instrumento, manualidad)",
      "Socialización activa",
      "Actividad física regular",
      "Sueño de calidad (7-8 horas)",
      "Dieta mediterránea",
      "Control de factores cardiovasculares"
    ]
  },
  deterioro_leve: {
    id: "deterioro_leve",
    name: "Programa para Deterioro Cognitivo Leve",
    source: "AOTA, Alzheimer's Association",
    triggers: ["mmse 18-23", "pfeiffer 3-7"],
    recommendations: [
      "Terapia de estimulación cognitiva grupal",
      "Ejercicios de memoria:",
      "  - Recordar listas de compras",
      "  - Relatar eventos del día",
      "  - Álbumes de fotos familiares",
      "Orientación a la realidad:",
      "  - Calendarios visibles",
      "  - Relojes grandes",
      "  - Rutinas estructuradas",
      "Actividades significativas previas",
      "Música y reminiscencia",
      "Terapia ocupacional"
    ]
  },
  deterioro_moderado: {
    id: "deterioro_moderado",
    name: "Intervención para Deterioro Cognitivo Moderado-Severo",
    source: "AOTA, Alzheimer's Association",
    triggers: ["mmse < 18", "pfeiffer >= 8"],
    recommendations: [
      "Simplificar el entorno",
      "Rutinas muy estructuradas",
      "Instrucciones simples, paso a paso",
      "Actividades de estimulación sensorial",
      "Musicoterapia",
      "Terapia con mascotas",
      "Validación emocional",
      "Evitar corrección excesiva",
      "Seguridad del entorno (prevenir fugas)",
      "Apoyo intensivo al cuidador"
    ]
  }
};

// ============ CAREGIVER RECOMMENDATIONS ============
export const CAREGIVER_LIBRARY = {
  general: {
    id: "general",
    name: "Recomendaciones Generales para Cuidadores",
    recommendations: [
      "Mantener rutinas diarias predecibles",
      "Comunicación clara y pausada",
      "Fomentar independencia en lo posible",
      "Celebrar pequeños logros",
      "Pedir ayuda cuando sea necesario",
      "Cuidar su propia salud",
      "Buscar grupos de apoyo para cuidadores"
    ]
  },
  signos_alarma: {
    id: "signos_alarma",
    name: "Signos de Alarma - Acudir a Urgencias",
    recommendations: [
      "Fiebre > 38°C que no cede con paracetamol",
      "Dolor de pecho o dificultad para respirar",
      "Confusión súbita o cambio brusco de comportamiento",
      "Caída con golpe en cabeza o incapacidad para levantarse",
      "Debilidad súbita en cara, brazo o pierna",
      "Dificultad para hablar o entender",
      "Pérdida de conocimiento",
      "Sangrado que no se detiene",
      "Vómitos o diarrea persistentes",
      "Incapacidad para tomar medicamentos o líquidos"
    ]
  },
  depresion: {
    id: "depresion",
    name: "Apoyo al Paciente con Depresión",
    triggers: ["yesavage >= 5"],
    recommendations: [
      "Acompañamiento empático, escucha activa",
      "Fomentar actividades placenteras",
      "Mantener socialización",
      "Exposición a luz solar",
      "Actividad física adaptada",
      "Vigilar ideación suicida",
      "Asegurar adherencia a medicación",
      "Evitar aislamiento"
    ]
  },
  demencia: {
    id: "demencia",
    name: "Cuidados en Demencia",
    triggers: ["mmse < 24"],
    recommendations: [
      "Mantener ambiente seguro y familiar",
      "Evitar cambios bruscos de rutina",
      "Usar ayudas visuales (carteles, fotos)",
      "Simplificar elecciones",
      "No discutir, redirigir con calma",
      "Validar emociones",
      "Vigilar riesgos: fuego, salidas, medicamentos",
      "Identificación con datos de contacto",
      "Planificar cuidados a largo plazo"
    ]
  }
};

// ============ GENERATE AUTOMATIC RECOMMENDATIONS ============
export const generateRecommendations = (scaleScores, diagnoses = [], conditions = []) => {
  const recommendations = {
    nutrition: [],
    exercise: [],
    fallPrevention: [],
    cognitive: [],
    caregiver: [],
    syndromes: detectGeriatricSyndromes(scaleScores)
  };

  // Nutrition recommendations
  recommendations.nutrition.push(NUTRITION_LIBRARY.general_geriatrico);
  
  if (scaleScores.mna_sf !== undefined) {
    if (scaleScores.mna_sf < 8) {
      recommendations.nutrition.push(NUTRITION_LIBRARY.malnutricion);
    } else if (scaleScores.mna_sf < 12) {
      recommendations.nutrition.push(NUTRITION_LIBRARY.riesgo_nutricional);
    }
  }

  const conditionsLower = conditions.map(c => c.toLowerCase());
  if (conditionsLower.some(c => c.includes('hipertensión') || c.includes('hta'))) {
    recommendations.nutrition.push(NUTRITION_LIBRARY.hipertension);
  }
  if (conditionsLower.some(c => c.includes('diabetes'))) {
    recommendations.nutrition.push(NUTRITION_LIBRARY.diabetes);
  }
  if (conditionsLower.some(c => c.includes('renal') || c.includes('erc'))) {
    recommendations.nutrition.push(NUTRITION_LIBRARY.insuficiencia_renal);
  }

  // Fall prevention
  if (scaleScores.morse !== undefined || scaleScores.tinetti !== undefined) {
    const morseScore = scaleScores.morse || 0;
    const tinettiScore = scaleScores.tinetti || 28;
    
    if (morseScore >= 45 || tinettiScore < 19) {
      recommendations.fallPrevention.push(FALL_PREVENTION_LIBRARY.alto_riesgo);
    } else if (morseScore >= 25 || tinettiScore < 24) {
      recommendations.fallPrevention.push(FALL_PREVENTION_LIBRARY.riesgo_moderado);
    } else {
      recommendations.fallPrevention.push(FALL_PREVENTION_LIBRARY.bajo_riesgo);
    }
  }

  // Exercise recommendations
  recommendations.exercise.push(EXERCISE_LIBRARY.general);
  
  if ((scaleScores.frail && scaleScores.frail >= 3) || (scaleScores.cfs && scaleScores.cfs >= 5)) {
    recommendations.exercise.push(EXERCISE_LIBRARY.fragilidad);
  }
  if (scaleScores.barthel !== undefined && scaleScores.barthel < 60) {
    recommendations.exercise.push(EXERCISE_LIBRARY.dependencia_moderada);
  }

  // Cognitive stimulation
  if (scaleScores.mmse !== undefined) {
    if (scaleScores.mmse >= 24) {
      recommendations.cognitive.push(COGNITIVE_LIBRARY.prevencion);
    } else if (scaleScores.mmse >= 18) {
      recommendations.cognitive.push(COGNITIVE_LIBRARY.deterioro_leve);
    } else {
      recommendations.cognitive.push(COGNITIVE_LIBRARY.deterioro_moderado);
    }
  }

  // Caregiver recommendations
  recommendations.caregiver.push(CAREGIVER_LIBRARY.general);
  recommendations.caregiver.push(CAREGIVER_LIBRARY.signos_alarma);
  
  if (scaleScores.yesavage && scaleScores.yesavage >= 5) {
    recommendations.caregiver.push(CAREGIVER_LIBRARY.depresion);
  }
  if (scaleScores.mmse !== undefined && scaleScores.mmse < 24) {
    recommendations.caregiver.push(CAREGIVER_LIBRARY.demencia);
  }

  return recommendations;
};

// ============ FORMAT RECOMMENDATIONS FOR DISPLAY ============
export const formatRecommendationsForPlan = (recommendations) => {
  let planText = "";

  if (recommendations.nutrition.length > 0) {
    planText += "=== PLAN NUTRICIONAL ===\n";
    recommendations.nutrition.forEach(rec => {
      planText += `\n${rec.name} (${rec.source || 'Guías internacionales'}):\n`;
      rec.recommendations.forEach(r => {
        planText += `• ${r}\n`;
      });
    });
  }

  if (recommendations.exercise.length > 0) {
    planText += "\n=== PLAN DE EJERCICIO ===\n";
    recommendations.exercise.forEach(rec => {
      planText += `\n${rec.name} (${rec.source || 'Guías internacionales'}):\n`;
      rec.recommendations.forEach(r => {
        planText += `• ${r}\n`;
      });
    });
  }

  if (recommendations.fallPrevention.length > 0) {
    planText += "\n=== PREVENCIÓN DE CAÍDAS ===\n";
    recommendations.fallPrevention.forEach(rec => {
      planText += `\n${rec.name} (${rec.source || 'CDC STEADI'}):\n`;
      rec.recommendations.forEach(r => {
        planText += `• ${r}\n`;
      });
    });
  }

  if (recommendations.cognitive.length > 0) {
    planText += "\n=== ESTIMULACIÓN COGNITIVA ===\n";
    recommendations.cognitive.forEach(rec => {
      planText += `\n${rec.name}:\n`;
      rec.recommendations.forEach(r => {
        planText += `• ${r}\n`;
      });
    });
  }

  return planText;
};

export const formatCaregiverRecommendations = (recommendations) => {
  let text = "";
  
  recommendations.caregiver.forEach(rec => {
    text += `\n${rec.name}:\n`;
    rec.recommendations.forEach(r => {
      text += `• ${r}\n`;
    });
  });

  return text;
};

// ============ GENERATE AUTOMATIC GERIATRIC ACTION PLAN ============
export const generateGeriatricActionPlan = (scaleScores, patientInfo = {}, diagnoses = [], conditions = []) => {
  const recommendations = generateRecommendations(scaleScores, diagnoses, conditions);
  const syndromes = recommendations.syndromes;
  
  const plan = {
    fecha: new Date().toISOString(),
    paciente: patientInfo,
    sindromes_detectados: syndromes,
    resumen_escalas: {},
    plan_nutricional: [],
    plan_ejercicio: [],
    plan_prevencion_caidas: [],
    plan_estimulacion_cognitiva: [],
    plan_cuidador: [],
    proxima_evaluacion: "",
    prioridades: []
  };

  // Build scale summary with interpretations
  const scaleInterpretations = {
    barthel: { name: "Índice de Barthel", max: 100, interpret: (s) => s >= 90 ? "Independiente" : s >= 60 ? "Dependencia leve" : s >= 40 ? "Dependencia moderada" : "Dependencia severa" },
    mmse: { name: "Mini-Mental (MMSE)", max: 30, interpret: (s) => s >= 24 ? "Normal" : s >= 18 ? "Deterioro leve" : s >= 10 ? "Deterioro moderado" : "Deterioro severo" },
    mna_sf: { name: "MNA-SF Nutricional", max: 14, interpret: (s) => s >= 12 ? "Normal" : s >= 8 ? "Riesgo de malnutrición" : "Malnutrición" },
    yesavage: { name: "Yesavage GDS-15", max: 15, interpret: (s) => interpretYesavageLabel(s) },
    katz: { name: "Katz ADL", max: 6, interpret: (s) => s === 6 ? "Independiente" : s >= 4 ? "Dependencia leve" : "Dependencia severa" },
    lawton: { name: "Lawton IADL", max: 8, interpret: (s) => s === 8 ? "Independiente" : s >= 5 ? "Dependencia leve" : "Dependencia severa" },
    morse: { name: "Escala de Morse", max: 125, interpret: (s) => s <= 24 ? "Riesgo bajo" : s <= 44 ? "Riesgo moderado" : "Riesgo alto de caídas" },
    tinetti: { name: "Tinetti POMA", max: 28, interpret: (s) => s >= 24 ? "Riesgo bajo" : s >= 19 ? "Riesgo moderado" : "Riesgo alto de caídas" },
    frail: { name: "Escala FRAIL", max: 5, interpret: (s) => s === 0 ? "Robusto" : s <= 2 ? "Pre-frágil" : "Frágil" },
    pfeiffer: { name: "Pfeiffer SPMSQ", max: 10, interpret: (s) => s <= 2 ? "Intacto" : s <= 4 ? "Deterioro leve" : s <= 7 ? "Deterioro moderado" : "Deterioro severo" },
    norton: { name: "Norton UPP", max: 20, interpret: (s) => s >= 14 ? "Riesgo mínimo" : s >= 12 ? "Riesgo evidente" : "Riesgo muy alto UPP" }
  };

  Object.entries(scaleScores).forEach(([scaleId, score]) => {
    if (scaleInterpretations[scaleId]) {
      const info = scaleInterpretations[scaleId];
      plan.resumen_escalas[scaleId] = {
        nombre: info.name,
        puntaje: score,
        maximo: info.max,
        interpretacion: info.interpret(score)
      };
    }
  });

  // Set priorities based on detected syndromes
  const priorityMap = {
    "riesgo_caidas": { priority: 1, action: "Implementar protocolo de prevención de caídas inmediatamente" },
    "malnutricion": { priority: 1, action: "Iniciar suplementación nutricional y evaluación por nutrición" },
    "fragilidad": { priority: 2, action: "Programa de ejercicio multicomponente supervisado" },
    "deterioro_cognitivo": { priority: 2, action: "Estimulación cognitiva y valoración neuropsicológica" },
    "depresion": { priority: 2, action: "Evaluación psiquiátrica y apoyo psicológico" },
    "dependencia_funcional": { priority: 3, action: "Terapia ocupacional y rehabilitación funcional" },
    "riesgo_upp": { priority: 1, action: "Protocolo de prevención de úlceras por presión" }
  };

  syndromes.forEach(syndrome => {
    if (priorityMap[syndrome.id]) {
      plan.prioridades.push({
        sindrome: syndrome.name,
        nivel: priorityMap[syndrome.id].priority,
        accion: priorityMap[syndrome.id].action
      });
    }
  });

  // Sort by priority
  plan.prioridades.sort((a, b) => a.nivel - b.nivel);

  // Add specific plans
  recommendations.nutrition.forEach(rec => {
    plan.plan_nutricional.push({
      titulo: rec.name,
      fuente: rec.source || "Guías internacionales",
      recomendaciones: rec.recommendations
    });
  });

  recommendations.exercise.forEach(rec => {
    plan.plan_ejercicio.push({
      titulo: rec.name,
      fuente: rec.source || "Guías OMS/APTA",
      recomendaciones: rec.recommendations
    });
  });

  recommendations.fallPrevention.forEach(rec => {
    plan.plan_prevencion_caidas.push({
      titulo: rec.name,
      fuente: rec.source || "CDC STEADI",
      recomendaciones: rec.recommendations
    });
  });

  recommendations.cognitive.forEach(rec => {
    plan.plan_estimulacion_cognitiva.push({
      titulo: rec.name,
      fuente: rec.source || "AOTA",
      recomendaciones: rec.recommendations
    });
  });

  recommendations.caregiver.forEach(rec => {
    plan.plan_cuidador.push({
      titulo: rec.name,
      recomendaciones: rec.recommendations
    });
  });

  // Set next evaluation date based on severity
  const hasHighRisk = syndromes.some(s => 
    ["riesgo_caidas", "malnutricion", "riesgo_upp"].includes(s.id)
  );
  const hasMediumRisk = syndromes.some(s => 
    ["fragilidad", "deterioro_cognitivo", "depresion"].includes(s.id)
  );

  if (hasHighRisk) {
    plan.proxima_evaluacion = "2-4 semanas (alto riesgo detectado)";
  } else if (hasMediumRisk) {
    plan.proxima_evaluacion = "4-8 semanas";
  } else {
    plan.proxima_evaluacion = "2-3 meses";
  }

  return plan;
};

// ============ FORMAT ACTION PLAN FOR DISPLAY ============
export const formatActionPlanSummary = (plan) => {
  let summary = "";

  if (plan.prioridades.length > 0) {
    summary += "PRIORIDADES CLÍNICAS:\n";
    plan.prioridades.forEach((p, idx) => {
      const urgency = p.nivel === 1 ? "🔴 URGENTE" : p.nivel === 2 ? "🟡 IMPORTANTE" : "🟢 SEGUIMIENTO";
      summary += `${idx + 1}. ${urgency} - ${p.sindrome}: ${p.accion}\n`;
    });
    summary += "\n";
  }

  if (Object.keys(plan.resumen_escalas).length > 0) {
    summary += "RESUMEN DE ESCALAS:\n";
    Object.values(plan.resumen_escalas).forEach(s => {
      summary += `• ${s.nombre}: ${s.puntaje}/${s.maximo} - ${s.interpretacion}\n`;
    });
    summary += "\n";
  }

  summary += `PRÓXIMA EVALUACIÓN: ${plan.proxima_evaluacion}\n`;

  return summary;
};

